// Libraries
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { cloneDeep, isEqual } from 'lodash';
import {
  Button,
  FormInputValueTypes,
  IBlockContent,
  ICLECredit,
  IWarning,
} from '@praktio/common-ui';
import { Helmet } from 'react-helmet';
import { Tab, Tabs, Container } from 'react-bootstrap';

// Ducks
import {
  addCourse,
  clearCourse,
  deleteCourse,
  getCourse,
  updateCourse,
} from '../../../ducks/courses';
import { RootState } from '../../../ducks/store';

// Components
import { CourseSettings } from './CourseSettings';
import CourseModules from './CourseModules';
import CourseCategories from './CourseCategories';
import { updateFormErrors, updateWarningsList } from '../../../utils/functions';
import { SubpageHeader } from '../../common/SubpageHeader/SubpageHeader';
import NetworkErrorModal from '../../common/modals/NetworkErrorModal/NetworkErrorModal';
import FeedbackModal from '../../common/FeedbackModal/FeedbackModal';
import DeleteModal from '../modals/DeleteModal/DeleteModal';

// Utils
import { GenericFormWarning, None } from '../../../utils/constants';

// CSS
import './Course.scss';

const Course = () => {
  const navigate = useNavigate();
  const params = useParams();
  const courseId = params.courseId as string;
  const isNew = courseId === 'new';
  const dispatch = useDispatch();
  const { data } = useSelector((state: RootState) => {
    return {
      data: state.courses.current,
    };
  });

  const prevCourse = useRef<ICourse | undefined>(data || ({} as ICourse));
  const [course, setCourse] = useState(data || ({} as ICourse));
  const [showDeleteCourseModal, setShowDeleteCourseModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(!isEqual(course, prevCourse.current));
  const [disableSave, setDisableSave] = useState(!hasChanges);
  const [savedChanges, setSavedChanges] = useState(false);
  const [warnings, setWarnings] = useState<IWarning[]>([]);
  const [courseSettingsFormErrors, setCourseSettingsFormErrors] = useState<IBootstrapFormErrors>();
  const [showNetworkErrorModal, setShowNetworkErrorModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [currentError, setCurrentError] = useState<string | undefined>();

  useEffect(() => {
    if (courseId !== data?._id) {
      if (!isNew) {
        dispatch(getCourse({ courseId }));
      } else {
        const newCourse: ICourse = { modules: [] };
        setCourse(newCourse);
      }
    }

    if (data && !isEqual(prevCourse.current, data)) {
      setCourse(data);
      setHasChanges(false);
    }

    prevCourse.current = cloneDeep(data);
  }, [data, isNew, courseId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearCourse());
    };
  }, [dispatch]);

  useEffect(() => {
    setDisableSave(!hasChanges);
    if (hasChanges && savedChanges) {
      setSavedChanges(false);
    }
  }, [hasChanges, savedChanges]);

  useEffect(() => {
    const newHasChanges = !isEqual(prevCourse.current, course);
    setHasChanges(newHasChanges);
  }, [course]);

  const onChange = (
    { fieldName, value }: { value?: string | boolean; fieldName?: string },
    clonedCourse?: ICourse,
  ) => {
    if (fieldName && clonedCourse) {
      clonedCourse[fieldName] = value;
    }
  };

  const onLogoChange = (files?: FileList, clonedCourse?: ICourse) => {
    if (clonedCourse) {
      clonedCourse.logo = files ? files[0] : undefined;
    }
  };

  const onCollaborationChange = (files?: FileList, clonedCourse?: ICourse) => {
    if (clonedCourse) {
      clonedCourse.collaboration = files ? files[0] : undefined;
    }
  };

  const onUpdateBlock = (label: string, content?: string, clonedCourse?: ICourse) => {
    if (label !== '' && (!content || content !== '')) {
      if (clonedCourse) {
        if (!clonedCourse.blocks) {
          clonedCourse.blocks = [];
        }
        const index = clonedCourse.blocks.findIndex((d) => d.label === label);

        if (index > -1) {
          if (content) {
            clonedCourse.blocks.splice(index, 1, { label, content });
          } else {
            clonedCourse.blocks.splice(index, 1);
          }
        } else if (content) {
          clonedCourse.blocks.push({ label, content });
        }
      }
    }
  };

  const onAddUpdateCLE = (state: string, credits?: string, clonedCourse?: ICourse) => {
    if (state !== None && (!credits || credits !== '')) {
      if (clonedCourse) {
        if (!clonedCourse.cleCredits) {
          clonedCourse.cleCredits = [];
        }
        const data = clonedCourse.cleCredits;
        const index = data.findIndex((c) => c.state === state);
        if (index > -1) {
          if (credits) {
            data.splice(index, 1, { state, credits });
          } else {
            data.splice(index, 1);
          }
        } else if (credits) {
          data.push({ state, credits });
        }
      }
    }
  };

  const handleChange = ({
    fieldName,
    value,
  }: {
    fieldName?: string;
    value?: FormInputValueTypes | IBlockContent | ICLECredit;
  }) => {
    const clonedCourse = cloneDeep(course);
    const clonedWarnings = cloneDeep(warnings);
    const clonedErrors = cloneDeep(courseSettingsFormErrors);
    switch (fieldName) {
      case 'logo':
        if (value instanceof FileList || !value) {
          onLogoChange(value as FileList | undefined, clonedCourse);
        }
        break;
      case 'collaboration':
        if (value instanceof FileList || !value) {
          onCollaborationChange(value as FileList | undefined, clonedCourse);
        }
        break;
      case 'blocks':
        let blockValue = value as BlockContent;
        onUpdateBlock(blockValue.label, blockValue.content, clonedCourse);
        break;
      case 'cles':
        let cleCredit = value as ICLECredit;
        onAddUpdateCLE(cleCredit.state, cleCredit.credits, clonedCourse);
        break;
      default:
        if (typeof value === 'string' || typeof value === 'boolean' || value === undefined)
          onChange({ fieldName, value }, clonedCourse);
    }
    setCourse(clonedCourse);
    updateFormErrors(fieldName || '', clonedErrors);
    updateWarningsList(clonedWarnings, clonedErrors);
    setCourseSettingsFormErrors(clonedErrors);
    setWarnings(clonedWarnings);
  };

  const findCourseFormErrors = ({ course }: { course: ICourse }) => {
    const newErrors = {} as IBootstrapFormErrors;
    const { title } = course;
    if (!title || title === '') {
      newErrors.title = 'This field cannot be blank.';
    }
    return newErrors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasChanges || !course) {
      return;
    }
    setDisableSave(true);
    const newErrors = await findCourseFormErrors({ course });
    if (Object.keys(newErrors).length > 0) {
      if (!warnings.some((warning) => warning.title === 'Form Error')) {
        setWarnings([...warnings, GenericFormWarning]);
      }
      setCourseSettingsFormErrors(newErrors);
    } else {
      try {
        if (course._id) {
          await dispatch(updateCourse(course));
        } else {
          await dispatch(addCourse(course, navigate));
        }

        setSavedChanges(true);
      } catch (e) {
        setShowNetworkErrorModal(true);
        setCurrentError(e as string);
      }
    }
    setDisableSave(false);
  };

  if (!course) {
    return null;
  }

  return (
    <div className="Course">
      <Helmet>
        <title>{data?.title || 'Course'} - Praktio</title>
      </Helmet>
      <SubpageHeader title={data?.title} savedChanges={savedChanges} unsavedChanges={hasChanges}>
        <Button
          form="course-settings-form"
          type="submit"
          btnStyle="primary"
          className="CourseSettingsSaveButton form-submit-button"
          disabled={!!disableSave}
        >
          SAVE
        </Button>
      </SubpageHeader>
      <Tabs
        defaultActiveKey="settings"
        id="admin-course-tabs"
        className="CourseSettings-tabs custom-tabs sticky-top"
      >
        <Tab eventKey="settings" title="Settings">
          <CourseSettings
            course={course}
            onChange={handleChange}
            setShowDeleteCourseModal={setShowDeleteCourseModal}
            handleSubmit={handleSubmit}
            warnings={warnings}
            errors={courseSettingsFormErrors}
            className="mt-4"
          />
        </Tab>
        <Tab eventKey="modules" title="Modules">
          <CourseModules course={course} setCourse={setCourse} />
        </Tab>
        <Tab eventKey="categories" title="Categories">
          <CourseCategories course={course} setCourse={setCourse} />
        </Tab>
      </Tabs>
      <DeleteModal
        type="Course"
        name={course.title as string}
        onHide={() => setShowDeleteCourseModal(false)}
        show={showDeleteCourseModal}
        dispatchAction={deleteCourse}
        dispatchData={{ courseId, navigate }}
      />
      <NetworkErrorModal
        show={showNetworkErrorModal}
        onHide={() => setShowNetworkErrorModal(false)}
        submitFeedback={() => {
          setShowNetworkErrorModal(false);
          setShowFeedbackModal(true);
        }}
      />
      <FeedbackModal
        show={showFeedbackModal}
        onHide={() => setShowFeedbackModal(false)}
        errorMessage={currentError}
      />
    </div>
  );
};

export default Course;
